import { type DragEvent, useId } from "react";

import {
  CaretDownIcon,
  CaretUpIcon,
  DotsSixVerticalIcon,
} from "@phosphor-icons/react";
import {
  Checkbox,
  Flex,
  IconButton,
  Text,
  VisuallyHidden,
} from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import { ColumnsPopover } from "~/v3-poc/components/ColumnsPopover";
import type {
  OrdersWidgetColumnId,
  OrdersWidgetColumnOption,
} from "./OrdersWidget.columns";
import type {
  OrdersColumnDropDirection,
  OrdersColumnMoveDirection,
} from "./useOrdersColumnVisibility";

export interface OrdersColumnsPopoverPresentationProps {
  columnOptions: readonly OrdersWidgetColumnOption[];
  visibleColumnIds: readonly OrdersWidgetColumnId[];
  requiredColumnIds: readonly OrdersWidgetColumnId[];
  onColumnVisibilityChange: (
    columnId: OrdersWidgetColumnId,
    isVisible: boolean,
  ) => void;
  onMoveColumn: (
    columnId: OrdersWidgetColumnId,
    direction: OrdersColumnMoveDirection,
  ) => void;
  onSelectAllColumns: () => void;
  onResetColumns: () => void;
  draggingColumnId: OrdersWidgetColumnId | null;
  dropIndicator: {
    columnId: OrdersWidgetColumnId;
    direction: OrdersColumnDropDirection;
  } | null;
  clearColumnDrag: () => void;
  handleDragStart: (
    event: DragEvent<HTMLElement>,
    columnId: OrdersWidgetColumnId,
  ) => void;
  handleDragEnd: () => void;
  handleDragOver: (
    event: DragEvent<HTMLDivElement>,
    targetColumnId: OrdersWidgetColumnId,
    isValidTarget: boolean,
  ) => void;
  handleDrop: (
    event: DragEvent<HTMLDivElement>,
    targetColumnId: OrdersWidgetColumnId,
  ) => void;
}

const isPinnedOrdersColumn = (columnId: OrdersWidgetColumnId): boolean =>
  columnId === "action" || columnId === "symbol";

const getOrdersColumnHelpId = (
  columnId: OrdersWidgetColumnId,
  isRequiredColumn: boolean,
  isFinalVisibleColumn: boolean,
  helpIds: {
    symbol: string;
    action: string;
    final: string;
  },
): string | undefined => {
  if (columnId === "symbol") {
    return helpIds.symbol;
  }
  if (isRequiredColumn) {
    return helpIds.action;
  }
  if (isFinalVisibleColumn) {
    return helpIds.final;
  }
  return undefined;
};

export const OrdersColumnsPopoverPresentation = ({
  columnOptions,
  visibleColumnIds,
  requiredColumnIds,
  onColumnVisibilityChange,
  onMoveColumn,
  onSelectAllColumns,
  onResetColumns,
  draggingColumnId,
  dropIndicator,
  clearColumnDrag,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDrop,
}: OrdersColumnsPopoverPresentationProps) => {
  const checkboxListId = useId();
  const finalColumnHelpId = useId();
  const symbolRequiredHelpId = useId();
  const actionRequiredHelpId = useId();
  const visibleColumnIdSet = new Set(visibleColumnIds);
  const requiredColumnIdSet = new Set(requiredColumnIds);
  const movableColumnIds: OrdersWidgetColumnId[] = columnOptions
    .map((option) => option.id)
    .filter(
      (columnId): boolean => columnId !== "action" && columnId !== "symbol",
    );
  const hideableColumnIds = columnOptions.filter(
    (option) => !requiredColumnIdSet.has(option.id) && option.id !== "symbol",
  );
  const visibleHideableCount = hideableColumnIds.filter((option) =>
    visibleColumnIdSet.has(option.id),
  ).length;

  return (
    <ColumnsPopover
      triggerVariant="icon"
      visibleCount={visibleHideableCount}
      totalCount={hideableColumnIds.length}
      onSelectAll={onSelectAllColumns}
      onReset={onResetColumns}
      onOpenChange={(open) => {
        if (!open) {
          clearColumnDrag();
        }
      }}
      footnote={
        <Text size="1" color="gray">
          Drag to reorder · uncheck to hide
        </Text>
      }
    >
      <Flex direction="column" gap="2" onPointerLeave={clearColumnDrag}>
        <VisuallyHidden id={finalColumnHelpId}>
          At least one column must remain visible.
        </VisuallyHidden>
        <VisuallyHidden id={symbolRequiredHelpId}>
          Symbol stays visible as the first data column.
        </VisuallyHidden>
        {requiredColumnIdSet.has("action") ? (
          <VisuallyHidden id={actionRequiredHelpId}>
            Action stays visible while order cancellation is available.
          </VisuallyHidden>
        ) : null}
        {columnOptions.map((option) => {
          const checkboxId = `${checkboxListId}-${option.id}`;
          const isVisible = visibleColumnIdSet.has(option.id);
          const isRequiredColumn =
            requiredColumnIdSet.has(option.id) || option.id === "symbol";
          const isFinalVisibleColumn =
            isVisible && visibleColumnIds.length === 1;
          const isDisabled = isRequiredColumn || isFinalVisibleColumn;
          const isPinned = isPinnedOrdersColumn(option.id);
          const movableIndex = movableColumnIds.indexOf(option.id);
          const isFirstMovable = movableIndex === 0;
          const isLastMovable = movableIndex === movableColumnIds.length - 1;

          const isDragging = draggingColumnId === option.id;
          const dropDirection =
            dropIndicator?.columnId === option.id
              ? dropIndicator.direction
              : undefined;

          return (
            <Flex
              key={option.id}
              align="center"
              gap="2"
              data-orders-column-row={option.id}
              data-drop-indicator={dropDirection}
              className={cn(
                isDragging &&
                  "rounded-[var(--radius-2)] bg-[var(--accent-a3)] opacity-60",
                dropDirection === "before" &&
                  "shadow-[inset_0_2px_0_0_var(--accent-9)]",
                dropDirection === "after" &&
                  "shadow-[inset_0_-2px_0_0_var(--accent-9)]",
              )}
              onDragOver={(event) =>
                handleDragOver(event, option.id, !isPinned)
              }
              onDrop={
                isPinned ? undefined : (event) => handleDrop(event, option.id)
              }
            >
              {isPinned ? (
                <Flex
                  as="span"
                  align="center"
                  justify="center"
                  flexShrink="0"
                  aria-hidden
                  className="cursor-not-allowed"
                >
                  <DotsSixVerticalIcon
                    size={14}
                    aria-hidden
                    className="pointer-events-none"
                  />
                </Flex>
              ) : (
                <Flex
                  as="span"
                  align="center"
                  justify="center"
                  flexShrink="0"
                  draggable
                  aria-label={`Drag ${option.label} to reorder`}
                  className="cursor-grab"
                  onDragStart={(event) => handleDragStart(event, option.id)}
                  onDragEnd={handleDragEnd}
                >
                  <DotsSixVerticalIcon
                    size={14}
                    aria-hidden
                    className="pointer-events-none"
                  />
                </Flex>
              )}
              <label
                htmlFor={checkboxId}
                className={cn(
                  "min-w-0 flex-1",
                  isDisabled ? "cursor-not-allowed" : "cursor-pointer",
                )}
              >
                <Flex align="center" gap="2">
                  <Checkbox
                    id={checkboxId}
                    checked={isVisible}
                    disabled={isDisabled}
                    aria-describedby={getOrdersColumnHelpId(
                      option.id,
                      isRequiredColumn,
                      isFinalVisibleColumn,
                      {
                        symbol: symbolRequiredHelpId,
                        action: actionRequiredHelpId,
                        final: finalColumnHelpId,
                      },
                    )}
                    onCheckedChange={(checked) =>
                      onColumnVisibilityChange(option.id, checked === true)
                    }
                  />
                  <Text size="2">{option.label}</Text>
                </Flex>
              </label>
              <Flex align="center" flexShrink="0">
                <IconButton
                  type="button"
                  size="1"
                  variant="ghost"
                  color="gray"
                  disabled={isPinned || isFirstMovable}
                  aria-label={`Move ${option.label} up`}
                  onClick={() => onMoveColumn(option.id, "up")}
                >
                  <CaretUpIcon size={12} aria-hidden />
                </IconButton>
                <IconButton
                  type="button"
                  size="1"
                  variant="ghost"
                  color="gray"
                  disabled={isPinned || isLastMovable}
                  aria-label={`Move ${option.label} down`}
                  onClick={() => onMoveColumn(option.id, "down")}
                >
                  <CaretDownIcon size={12} aria-hidden />
                </IconButton>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </ColumnsPopover>
  );
};
